import { Context, Inject } from '@nuxt/types/app';
import { transformAlertsData } from '~/transforms/transformAlertsData';
import { GlobalsStoryblok, MarketStoryblok } from '~/types/storyblok-generated';
import { getValidLocale, i18nLocales } from '~/utility/i18n';

export default async ({ app, route }: Context, inject: Inject) => {
  const locale = getValidLocale(route.path.split('/')[1]);
  let wasAlreadyUpdated = false;

  // Check if the locale is valid, otherwise fallback to default
  if (!i18nLocales.find(l => l.code === locale.code)) {
    locale.code = i18nLocales[0].code;
  }

  try {
    const [prefetchedGlobalsData, prefetchedMarketSettingsData] = await Promise.all([
      require(`~/assets/storyblok-data/globals_${locale.code}.json`),
      require(`~/assets/storyblok-data/market-settings_${locale.code}.json`),
    ]);

    inject('globals', prefetchedGlobalsData?.story?.content ?? {});
    inject('marketSettings', prefetchedMarketSettingsData?.story?.content ?? {});
    inject('alerts', prefetchedMarketSettingsData.story.content.alerts);
  } catch (error) {
    console.log('No prefetched data for', locale.code);
  }

  async function updateGlobalData() {
    if (wasAlreadyUpdated) {
      return;
    }
    wasAlreadyUpdated = true;
    try {
      const [globalsRes, marketSettingsRes] = await Promise.all([
        app.$storyapi.get(`cdn/stories/globals`, {
          language: locale.lang,
          version: process.env.STORYBLOK_CONTENT_VERSION,
          resolve_links: 'url',
          cv: Date.now(),
        }),
        app.$storyapi.get(`cdn/stories/${locale.code}/market-settings`, {
          version: process.env.STORYBLOK_CONTENT_VERSION,
          resolve_links: 'url',
          resolve_relations: 'alertItem.pages',
          cv: Date.now(),
        }),
      ]);
      inject('globals', globalsRes.data?.story?.content ?? {});
      inject('marketSettings', marketSettingsRes.data?.story?.content ?? {});
      const transformedAlerts = transformAlertsData(marketSettingsRes.data?.story?.content?.alerts);
      if (transformedAlerts) {
        inject('alerts', transformedAlerts);
      }
    } catch (error) {
      console.error('Error fetching global data from Storyblok');
    }
  }

  inject('updateGlobalData', updateGlobalData);
};

declare module '@nuxt/types' {
  interface Context {
    $globals: GlobalsStoryblok;
    $marketSettings: MarketStoryblok;
    $dataSources: any;
    $alerts: any;
    $updateGlobalData: () => Promise<void>;
  }
}

declare module 'vue/types/vue' {
  interface Vue {
    $globals: GlobalsStoryblok;
    $marketSettings: MarketStoryblok;
    $dataSources: any;
    $alerts: any;
    $updateGlobalData: () => Promise<void>;
  }
}
